import { useState } from 'react';
import { EmptyState, SplitBar } from '../components/common';
import { formatRelativeTime, getPostSplit, getProfileIdentity, getTopicLabel, getUserSettings, isOpenThought } from '../utils/hixrs';
import { getTotalComments } from '../utils/threads';

export function PublicProfileScreen({ target, users, posts, currentUser, profile, onBack, onFollowToggle, requireLogin, onOpenPost }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [socialList, setSocialList] = useState(null);

  if (!target) {
    return (
      <div className="screen-wrap">
        <div className="profile-page-wrap">
          <EmptyState
            eyebrow="Profile"
            title="Profile not found"
            body="This profile may have been removed or is no longer available."
            actionLabel="Back to Hixs"
            onAction={onBack} />
        </div>
      </div>
    );
  }

  const identity = getProfileIdentity(target, users);
  const { uid, displayName, handle, photoURL, bannerURL, bio, following } = identity;
  const initial = displayName?.[0]?.toUpperCase() || 'H';
  const isSelf = Boolean(uid && currentUser?.uid === uid);
  const settings = getUserSettings(identity.saved);
  const privacy = settings.privacy;
  const isPrivateProfile = !isSelf && privacy.publicProfile === false;
  const canFollow = Boolean(uid && !isSelf);
  const isFollowing = Boolean(uid && Array.isArray(profile?.following) && profile.following.includes(uid));
  const profilePosts = posts.filter(post => uid ? post.authorId === uid : post.authorName === displayName);
  const followersCount = uid
    ? users.filter(user => Array.isArray(user.following) && user.following.includes(uid)).length
    : 0;
  const followersList = uid
    ? users.filter(user => Array.isArray(user.following) && user.following.includes(uid))
    : [];
  const followingList = following.map(id => users.find(user => user.uid === id || user.id === id)).filter(Boolean);
  const visibleSocialList = socialList === 'followers' ? followersList : followingList;
  const socialTitle = socialList === 'followers' ? 'Followers' : 'Following';
  const profileLockIns = profilePosts.reduce((sum, post) => sum + Number(post.totalVotes || 0), 0);
  const profileCommentCount = profilePosts.reduce((sum, post) => sum + getTotalComments(post.comments || []), 0);
  const topicCounts = profilePosts.reduce((acc, post) => {
    const label = getTopicLabel(post.topic);
    acc[label] = (acc[label] || 0) + 1;
    return acc;
  }, {});
  const topTopic = Object.entries(topicCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || 'New voice';
  const latestPostTime = profilePosts[0] ? formatRelativeTime(profilePosts[0].createdAt) : 'No Hixs yet';

  if (isPrivateProfile) {
    return (
      <div className="screen-wrap profile-page-screen">
        <div className="profile-page-wrap private-profile-wrap">
          <section className="profile-page-main">
            <button className="profile-page-back" onClick={onBack}>Back</button>
            <div className={`profile-page-hero private-profile-hero ${bannerURL ? 'has-profile-banner' : ''}`} style={bannerURL ? { '--profile-banner': `url("${bannerURL}")` } : {}}>
              {bannerURL && <img className="profile-banner-img" src={bannerURL} alt="" aria-hidden="true" />}
              <div className="profile-page-avatar">
                {photoURL ? <img src={photoURL} alt="profile" /> : initial}
              </div>
              <div className="profile-page-title-block">
                <div className="profile-page-name-row">
                  <h1>{displayName}</h1>
                  <span>Private</span>
                </div>
                <div className="profile-page-handle">{handle}</div>
                <p>This user profile is private.</p>
              </div>
            </div>
            <EmptyState
              eyebrow="Private"
              title="This profile is private"
              body="The owner has limited what other people can see on their profile."
              actionLabel="Back to Hixs"
              onAction={onBack} />
          </section>
        </div>
      </div>
    );
  }

  const collectUserComments = (items = [], post, path = []) => items.flatMap((comment, index) => {
    const commentPath = [...path, index];
    const belongsToUser = uid ? comment.userId === uid : comment.user === displayName;
    const own = belongsToUser && !comment.deleted
      ? [{ comment, post, path: commentPath }]
      : [];
    return [...own, ...collectUserComments(comment.replies || [], post, commentPath)];
  });

  const profileComments = posts.flatMap(post => collectUserComments(post.comments || [], post));
  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'posts', label: 'Posts' },
    { id: 'comments', label: 'Comments' }
  ].filter(tab => privacy.showLockHistory !== false || tab.id !== 'comments');
  const safeActiveTab = tabs.some(tab => tab.id === activeTab) ? activeTab : 'overview';

  const openPost = (post) => {
    onOpenPost(post);
  };

  const renderPostCard = (post) => {
    const split = getPostSplit(post);
    const thought = isOpenThought(post);
    return (
      <button key={post.id} className={`profile-page-post-card ${thought ? 'profile-page-thought-card' : ''}`} onClick={() => openPost(post)}>
        {post.image && <img src={post.image} alt="Hix" />}
        <span>{post.question}</span>
        <small>{getTopicLabel(post.topic)} - {thought ? `${getTotalComments(post.comments || [])} comments` : `${post.totalVotes || 0} lock-ins`} - {formatRelativeTime(post.createdAt)}</small>
        {thought ? <em className="my-thought-badge">Open Thought</em> : <SplitBar pctA={split.pctA} className="profile-page-split" />}
      </button>
    );
  };

  return (
    <div className="screen-wrap profile-page-screen">
      <div className="profile-page-wrap">
        <section className="profile-page-main">
          <button className="profile-page-back" onClick={onBack}>Back</button>
          <div className={`profile-page-hero ${bannerURL ? 'has-profile-banner' : ''}`} style={bannerURL ? { '--profile-banner': `url("${bannerURL}")` } : {}}>
            {bannerURL && <img className="profile-banner-img" src={bannerURL} alt="" aria-hidden="true" />}
            <div className="profile-page-avatar">
              {photoURL ? <img src={photoURL} alt="profile" /> : initial}
            </div>
            <div className="profile-page-title-block">
              <div className="profile-page-name-row">
                <h1>{displayName}</h1>
                <span>{topTopic}</span>
              </div>
              <div className="profile-page-handle">{handle}</div>
              <p>{bio}</p>
            </div>
            <div className="profile-page-actions">
              {isSelf ? (
                <button className="profile-follow-btn self" disabled>Your profile</button>
              ) : canFollow ? (
                <button className={`profile-follow-btn ${isFollowing ? 'following' : ''}`} onClick={() => requireLogin(() => onFollowToggle(uid))}>
                  {isFollowing ? 'Following' : 'Follow'}
                </button>
              ) : (
                <button className="profile-follow-btn self" disabled>Profile only</button>
              )}
            </div>
          </div>

          <div className="profile-page-tabs">
            {tabs.map(tab => (
              <button key={tab.id} className={safeActiveTab === tab.id ? 'active' : ''} onClick={() => setActiveTab(tab.id)}>
                {tab.label}
              </button>
            ))}
          </div>

          {safeActiveTab === 'overview' && (
            <div className="profile-page-tab-content">
              {profilePosts.length === 0 ? (
                <EmptyState
                  eyebrow="New voice"
                  title={`${displayName} has not posted yet`}
                  body="Their profile will fill up as they create Hixs and join more discussions." />
              ) : (
                <div className="profile-page-grid-list">
                  {profilePosts.slice(0, 4).map(renderPostCard)}
                </div>
              )}
            </div>
          )}

          {safeActiveTab === 'posts' && (
            <div className="profile-page-tab-content">
              {profilePosts.length === 0 ? (
                <EmptyState
                  compact
                  eyebrow="Posts"
                  title="No Hixs posted yet"
                  body="When this profile creates Hixs, they will appear here." />
              ) : (
                <div className="profile-page-grid-list">
                  {profilePosts.map(renderPostCard)}
                </div>
              )}
            </div>
          )}

          {safeActiveTab === 'comments' && (
            <div className="profile-page-tab-content">
              {profileComments.length === 0 ? (
                <EmptyState
                  compact
                  eyebrow="Comments"
                  title="No public comments yet"
                  body="Replies and comments from this profile will appear here." />
              ) : (
                <div className="profile-page-comment-list">
                  {profileComments.slice(0, 20).map(({ comment, post, path }) => (
                    <button key={`${post.id}-${path.join('-')}`} className="profile-page-comment-card" onClick={() => openPost(post)}>
                      <strong>{comment.text || 'Image reply'}</strong>
                      <span>On: {post.question}</span>
                      <small>{formatRelativeTime(comment.createdAt)}</small>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </section>

        <aside className="profile-page-about">
          <div className="profile-page-about-card">
            <div className="profile-page-about-title">About</div>
            <div className="profile-page-about-grid">
              {privacy.showFollowers !== false && <button type="button" onClick={() => setSocialList('followers')}><strong>{followersCount}</strong><span>Followers</span></button>}
              {privacy.showFollowing !== false && <button type="button" onClick={() => setSocialList('following')}><strong>{following.length}</strong><span>Following</span></button>}
              <div><strong>{profilePosts.length}</strong><span>Hixs</span></div>
              {privacy.showLockHistory !== false && <div><strong>{profileLockIns}</strong><span>Lock-ins</span></div>}
              {privacy.showLockHistory !== false && <div><strong>{profileCommentCount}</strong><span>Comments</span></div>}
              <div><strong>{latestPostTime}</strong><span>Latest</span></div>
            </div>
            {privacy.showOpinionPassport !== false && (
              <>
                <div className="profile-page-divider"></div>
                <div className="profile-page-about-title">Identity</div>
                <div className="profile-page-badges">
                  <span>{topTopic}</span>
                  <span>{profilePosts.length > 2 ? 'Creator' : 'New voice'}</span>
                  <span>{profileLockIns > 10 ? 'Active' : 'Building'}</span>
                </div>
              </>
            )}
            {socialList && (
              <div className="profile-social-panel public-profile-social-panel">
                <div className="profile-section-head">
                  <div>
                    <div className="history-title">{socialTitle}</div>
                    <div className="profile-section-sub">{visibleSocialList.length} {visibleSocialList.length === 1 ? 'person' : 'people'}</div>
                  </div>
                  <button type="button" className="profile-secondary-btn" onClick={() => setSocialList(null)}>Close</button>
                </div>
                {visibleSocialList.length === 0 ? (
                  <EmptyState compact eyebrow={socialTitle} title={`No ${socialTitle.toLowerCase()} yet`} body="This list will fill as people follow each other." />
                ) : (
                  <div className="profile-social-list">
                    {visibleSocialList.map(item => {
                      const name = item.displayName || item.email || 'Hixrs user';
                      const handleText = item.username ? `@${item.username}` : item.email || '@hixr';
                      return (
                        <div key={item.uid || item.id || handleText} className="profile-social-item">
                          <div className="profile-social-avatar">{item.photoURL ? <img src={item.photoURL} alt={name} /> : name[0]?.toUpperCase() || 'H'}</div>
                          <div><strong>{name}</strong><span>{handleText}</span></div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
